import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Grid, List, MapPin, Info, DollarSign, Filter, X, RotateCcw, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { InteriorModel } from '../../types/interior';
interface ModelSelectionScreenProps {
  models: InteriorModel[];
  onSelectModel: (modelId: string) => void;
  onBack: () => void;
}
interface FilterState {
  houseTypes: Set<string>;
  garageTypes: Set<string>;
  beds: number | null;
  baths: number | null;
  priceRange: [number, number];
  sqftRange: [number, number];
  communities: Set<string>;
}
const initialFilters: FilterState = {
  houseTypes: new Set(),
  garageTypes: new Set(),
  beds: null,
  baths: null,
  priceRange: [200000, 800000],
  sqftRange: [600, 2600],
  communities: new Set()
};
export function ModelSelectionScreen({
  models,
  onSelectModel,
  onBack
}: ModelSelectionScreenProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  // Filter Logic
  const filteredModels = useMemo(() => {
    return models.filter(model => {
      if (filters.houseTypes.size > 0 && !filters.houseTypes.has(model.houseType)) return false;
      if (filters.garageTypes.size > 0 && !filters.garageTypes.has(model.garageType)) return false;
      if (filters.beds && model.beds < filters.beds) return false;
      if (filters.baths && model.baths < filters.baths) return false;
      if (model.price < filters.priceRange[0] || model.price > filters.priceRange[1]) return false;
      if (model.sqft < filters.sqftRange[0] || model.sqft > filters.sqftRange[1]) return false;
      if (filters.communities.size > 0 && !filters.communities.has(model.community)) return false;
      return true;
    });
  }, [models, filters]);
  const activeFilterCount = filters.houseTypes.size + filters.garageTypes.size + (filters.beds ? 1 : 0) + (filters.baths ? 1 : 0) + (filters.priceRange[0] !== initialFilters.priceRange[0] || filters.priceRange[1] !== initialFilters.priceRange[1] ? 1 : 0) + (filters.sqftRange[0] !== initialFilters.sqftRange[0] || filters.sqftRange[1] !== initialFilters.sqftRange[1] ? 1 : 0) + filters.communities.size;
  return <div className="relative w-full h-screen bg-slate-900 overflow-hidden font-sans" data-id="element-395">
      {/* Background */}
      <div className="absolute inset-0" data-id="element-396">
        <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1920&q=80" alt="Interior background" className="w-full h-full object-cover opacity-20 blur-sm" data-id="element-397" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 to-slate-900/70" data-id="element-398" />
      </div>

      {/* Header */}
      <div className="relative z-10 p-6 border-b border-white/10 bg-slate-900/50 backdrop-blur-xl flex items-center justify-between" data-id="element-399">
        <button onClick={onBack} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors group" data-id="element-400">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-401" />
          <span className="text-sm font-medium tracking-wide" data-id="element-402">BACK</span>
        </button>

        <div className="text-center" data-id="element-403">
          <h1 className="text-2xl font-bold text-white mb-1 uppercase tracking-[0.2em]" data-id="element-404">
            Landmark Homes
          </h1>
          <div className="h-0.5 w-16 bg-gradient-to-r from-cyan-500 to-blue-600 mx-auto rounded-full" data-id="element-405" />
        </div>

        <div className="flex items-center gap-3" data-id="element-406">
          <div className="flex items-center bg-slate-800/80 rounded-lg p-1 border border-white/10" data-id="element-407">
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-cyan-500 text-white shadow-lg' : 'text-white/50 hover:text-white hover:bg-white/5'}`} data-id="element-408">
              <Grid className="w-4 h-4" data-id="element-409" />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-cyan-500 text-white shadow-lg' : 'text-white/50 hover:text-white hover:bg-white/5'}`} data-id="element-410">
              <List className="w-4 h-4" data-id="element-411" />
            </button>
          </div>

          <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all ${showFilters || activeFilterCount > 0 ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-400' : 'bg-slate-800/80 border-white/10 text-white/70 hover:text-white hover:border-white/30'}`} data-id="element-412">
            <Filter className="w-4 h-4" data-id="element-413" />
            <span className="text-sm font-medium uppercase tracking-wide" data-id="element-414">
              Filters
            </span>
            {activeFilterCount > 0 && <span className="bg-cyan-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center" data-id="element-415">
                {activeFilterCount}
              </span>}
          </button>
        </div>
      </div>

      {/* Main layout: Filter sidebar + Content */}
      <div className="relative z-0 flex h-[calc(100vh-90px)]" data-id="element-416">
        {/* Filter Sidebar - Left, pushes content */}
        <motion.div initial={false} animate={{
        width: showFilters ? 320 : 0,
        opacity: showFilters ? 1 : 0
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="flex-shrink-0 overflow-hidden border-r border-white/10 bg-slate-900/70 backdrop-blur-xl" data-id="element-417">
          <div className="w-[320px] h-full" data-id="element-418">
            <FilterPanel filters={filters} onChange={setFilters} onClose={() => setShowFilters(false)} onReset={() => setFilters(initialFilters)} data-id="element-419" />
          </div>
        </motion.div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-8" data-id="element-420">
          <div className="max-w-7xl mx-auto" data-id="element-421">
            <div className="flex items-baseline justify-between mb-8" data-id="element-422">
              <h2 className="text-xl font-light text-white uppercase tracking-widest" data-id="element-423">
                Featured Models
              </h2>
              <span className="text-sm text-white/50" data-id="element-424">
                {filteredModels.length} Results Found
              </span>
            </div>

            <AnimatePresence mode="wait" data-id="element-425">
              {viewMode === 'grid' ? <motion.div key="grid" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-426">
                  {filteredModels.map((model, index) => <ModelCardGrid key={model.id} model={model} index={index} onSelect={() => onSelectModel(model.id)} data-id="element-427" />)}
                </motion.div> : <motion.div key="list" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="space-y-4" data-id="element-428">
                  {filteredModels.map((model, index) => <ModelCardList key={model.id} model={model} index={index} onSelect={() => onSelectModel(model.id)} data-id="element-429" />)}
                </motion.div>}
            </AnimatePresence>

            {filteredModels.length === 0 && <div className="text-center py-20" data-id="element-430">
                <p className="text-white/50 text-lg" data-id="element-431">
                  No models found matching your criteria.
                </p>
                <button onClick={() => setFilters(initialFilters)} className="mt-4 text-cyan-400 hover:text-cyan-300 underline underline-offset-4" data-id="element-432">
                  Clear all filters
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}
// --- Subcomponents ---
function ModelCardGrid({
  model,
  index,
  onSelect
}: {
  model: InteriorModel;
  index: number;
  onSelect: () => void;
}) {
  return <motion.button initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.05
  }} onClick={onSelect} className="group relative flex w-full bg-slate-800/40 backdrop-blur-md rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 hover:bg-slate-800/60 transition-all duration-300 text-left h-48" data-id="element-433">
      {/* Image Section */}
      <div className="relative w-[40%] h-full overflow-hidden" data-id="element-434">
        <img src={model.thumbnailUrl} alt={model.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-435" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-900/80" data-id="element-436" />
      </div>

      {/* Content Section */}
      <div className="flex-1 p-5 flex flex-col justify-center relative" data-id="element-437">
        <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-transparent via-white/10 to-transparent" data-id="element-438" />

        <h3 className="text-2xl font-bold text-white mb-1 uppercase tracking-wider group-hover:text-cyan-400 transition-colors" data-id="element-439">
          {model.name}
        </h3>
        <p className="text-xs font-medium text-cyan-500/90 mb-4 uppercase tracking-wide" data-id="element-440">
          {model.sqft.toLocaleString()} SQ FT <span className="mx-1" data-id="element-441">|</span>{' '}
          {model.beds} BEDS <span className="mx-1" data-id="element-442">|</span> {model.baths} BATHS
        </p>

        <div className="space-y-2" data-id="element-443">
          <div className="flex items-center gap-2 text-white/60 text-xs" data-id="element-444">
            <MapPin className="w-3.5 h-3.5 text-cyan-500/70" data-id="element-445" />
            <span className="truncate" data-id="element-446">{model.community}</span>
          </div>
          <div className="flex items-center gap-2 text-white/60 text-xs" data-id="element-447">
            <Info className="w-3.5 h-3.5 text-cyan-500/70" data-id="element-448" />
            <span className="truncate" data-id="element-449">{model.houseType}</span>
          </div>
          <div className="flex items-center gap-2 text-white/80 text-sm font-medium mt-1" data-id="element-450">
            <DollarSign className="w-3.5 h-3.5 text-cyan-500" data-id="element-451" />
            <span data-id="element-452">${(model.price / 1000).toFixed(0)}k</span>
          </div>
        </div>
      </div>
    </motion.button>;
}
function ModelCardList({
  model,
  index,
  onSelect
}: {
  model: InteriorModel;
  index: number;
  onSelect: () => void;
}) {
  return <motion.button initial={{
    opacity: 0,
    x: -20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay: index * 0.05
  }} onClick={onSelect} className="group w-full flex items-center bg-slate-800/40 backdrop-blur-md rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 hover:bg-slate-800/60 transition-all duration-300 text-left h-24 pr-6" data-id="element-453">
      <div className="w-32 h-full relative overflow-hidden flex-shrink-0" data-id="element-454">
        <img src={model.thumbnailUrl} alt={model.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-455" />
      </div>

      <div className="flex-1 px-6 grid grid-cols-[1fr_auto_auto] items-center gap-6" data-id="element-456">
        <div className="min-w-0" data-id="element-457">
          <h3 className="text-lg font-bold text-white uppercase tracking-wider group-hover:text-cyan-400 transition-colors truncate" data-id="element-458">
            {model.name}
          </h3>
          <p className="text-xs text-white/50 truncate" data-id="element-459">{model.houseType}</p>
        </div>

        <div className="flex items-center gap-8 text-sm text-white/70 flex-shrink-0" data-id="element-460">
          <div className="flex flex-col items-center w-14" data-id="element-461">
            <span className="text-xs text-white/40 uppercase" data-id="element-462">Sq Ft</span>
            <span className="font-medium" data-id="element-463">{model.sqft.toLocaleString()}</span>
          </div>
          <div className="flex flex-col items-center w-10" data-id="element-464">
            <span className="text-xs text-white/40 uppercase" data-id="element-465">Beds</span>
            <span className="font-medium" data-id="element-466">{model.beds}</span>
          </div>
          <div className="flex flex-col items-center w-10" data-id="element-467">
            <span className="text-xs text-white/40 uppercase" data-id="element-468">Baths</span>
            <span className="font-medium" data-id="element-469">{model.baths}</span>
          </div>
        </div>

        <div className="text-right flex-shrink-0 w-24" data-id="element-470">
          <span className="block text-lg font-bold text-cyan-400" data-id="element-471">
            ${(model.price / 1000).toFixed(0)}k
          </span>
          <span className="text-xs text-white/40" data-id="element-472">{model.community}</span>
        </div>
      </div>
    </motion.button>;
}
// --- Filter Panel ---
function FilterPanel({
  filters,
  onChange,
  onClose,
  onReset
}: {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  onClose: () => void;
  onReset: () => void;
}) {
  return <div className="flex flex-col h-full" data-id="element-473">
      {/* Header */}
      <div className="p-5 border-b border-white/10 flex items-center justify-between bg-slate-900/50" data-id="element-474">
        <h2 className="text-lg font-light text-white" data-id="element-475">
          Filter <span className="font-bold text-cyan-400" data-id="element-476">Models</span>
        </h2>
        <div className="flex items-center gap-3" data-id="element-477">
          <button onClick={onReset} className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-slate-400 hover:text-white transition-colors" data-id="element-478">
            <RotateCcw className="w-3 h-3" data-id="element-479" />
            Reset
          </button>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors" data-id="element-480">
            <X className="w-4 h-4" data-id="element-481" />
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-8" data-id="element-482">
        <FilterSection title="House Type" data-id="element-483">
          <div className="space-y-2" data-id="element-484">
            {['Front Garage Home', 'Laned Home', 'Duplex', 'Townhome'].map(type => <Checkbox key={type} label={type} checked={filters.houseTypes.has(type)} onChange={() => {
            const newSet = new Set(filters.houseTypes);
            if (newSet.has(type)) newSet.delete(type);else newSet.add(type);
            onChange({
              ...filters,
              houseTypes: newSet
            });
          }} data-id="element-485" />)}
          </div>
        </FilterSection>

        <FilterSection title="Garage Type" data-id="element-486">
          <div className="space-y-2" data-id="element-487">
            {['Single Attached', 'Double Attached', 'Double Detached', 'No Garage'].map(type => <Checkbox key={type} label={type} checked={filters.garageTypes.has(type)} onChange={() => {
            const newSet = new Set(filters.garageTypes);
            if (newSet.has(type)) newSet.delete(type);else newSet.add(type);
            onChange({
              ...filters,
              garageTypes: newSet
            });
          }} data-id="element-488" />)}
          </div>
        </FilterSection>

        <FilterSection title="Bedrooms" data-id="element-489">
          <div className="flex gap-2" data-id="element-490">
            {[1, 2, 3, 4].map(num => <button key={num} onClick={() => onChange({
            ...filters,
            beds: filters.beds === num ? null : num
          })} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${filters.beds === num ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20' : 'bg-slate-800/50 border border-white/5 text-slate-400 hover:border-white/20 hover:text-white'}`} data-id="element-491">
                {num}
                {num === 4 ? '+' : ''}
              </button>)}
          </div>
        </FilterSection>

        <FilterSection title="Bathrooms" data-id="element-492">
          <div className="flex gap-2" data-id="element-493">
            {[1, 1.5, 2, 2.5, 3].map(num => <button key={num} onClick={() => onChange({
            ...filters,
            baths: filters.baths === num ? null : num
          })} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${filters.baths === num ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20' : 'bg-slate-800/50 border border-white/5 text-slate-400 hover:border-white/20 hover:text-white'}`} data-id="element-494">
                {num}
                {num === 3 ? '+' : ''}
              </button>)}
          </div>
        </FilterSection>

        <FilterSection title="Price Range" data-id="element-495">
          <RangeSlider min={200000} max={800000} step={10000} value={filters.priceRange} onChange={val => onChange({
          ...filters,
          priceRange: val
        })} format={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-496" />
        </FilterSection>

        <FilterSection title="Square Footage" data-id="element-497">
          <RangeSlider min={600} max={2600} step={50} value={filters.sqftRange} onChange={val => onChange({
          ...filters,
          sqftRange: val
        })} format={v => `${v.toLocaleString()} sqft`} data-id="element-498" />
        </FilterSection>

        <FilterSection title="Community" data-id="element-499">
          <div className="space-y-2" data-id="element-500">
            {['Edmonton', 'Calgary'].map(city => <Checkbox key={city} label={city} checked={filters.communities.has(city)} onChange={() => {
            const newSet = new Set(filters.communities);
            if (newSet.has(city)) newSet.delete(city);else newSet.add(city);
            onChange({
              ...filters,
              communities: newSet
            });
          }} data-id="element-501" />)}
          </div>
        </FilterSection>
      </div>
    </div>;
}
function FilterSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(true);
  return <div className="border-b border-white/5 pb-6 last:border-0" data-id="element-502">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-between w-full mb-4 group" data-id="element-503">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 group-hover:text-slate-300 transition-colors" data-id="element-504">
          {title}
        </span>
        {isOpen ? <ChevronUp className="w-4 h-4 text-slate-600 group-hover:text-slate-400" data-id="element-505" /> : <ChevronDown className="w-4 h-4 text-slate-600 group-hover:text-slate-400" data-id="element-506" />}
      </button>
      <AnimatePresence data-id="element-507">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-508">
            {children}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function Checkbox({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return <label className="flex items-center gap-3 cursor-pointer group" data-id="element-509">
      <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${checked ? 'bg-cyan-500 border-cyan-500' : 'bg-slate-800/50 border-white/20 group-hover:border-white/40'}`} data-id="element-510">
        {checked && <Check className="w-3.5 h-3.5 text-white" data-id="element-511" />}
      </div>
      <input type="checkbox" className="hidden" checked={checked} onChange={onChange} data-id="element-512" />
      <span className={`text-sm transition-colors ${checked ? 'text-white font-medium' : 'text-slate-400 group-hover:text-slate-300'}`} data-id="element-513">
        {label}
      </span>
    </label>;
}
function RangeSlider({
  min,
  max,
  step,
  value,
  onChange,
  format
}: {
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (val: [number, number]) => void;
  format: (v: number) => string;
}) {
  const [minVal, maxVal] = value;
  const minPercent = (minVal - min) / (max - min) * 100;
  const maxPercent = (maxVal - min) / (max - min) * 100;
  return <div className="pt-6 pb-2 px-2" data-id="element-514">
      <div className="relative h-1.5 bg-slate-800 rounded-full mb-6" data-id="element-515">
        <div className="absolute h-full bg-cyan-500 rounded-full" style={{
        left: `${minPercent}%`,
        width: `${maxPercent - minPercent}%`
      }} data-id="element-516" />

        {/* Thumbs */}
        <input type="range" min={min} max={max} step={step} value={minVal} onChange={e => {
        const val = Math.min(Number(e.target.value), maxVal - step);
        onChange([val, maxVal]);
      }} className="absolute -left-2 w-[calc(100%+16px)] h-6 -top-2.5 opacity-0 cursor-pointer z-10" data-id="element-517" />
        <input type="range" min={min} max={max} step={step} value={maxVal} onChange={e => {
        const val = Math.max(Number(e.target.value), minVal + step);
        onChange([minVal, val]);
      }} className="absolute -left-2 w-[calc(100%+16px)] h-6 -top-2.5 opacity-0 cursor-pointer z-10" data-id="element-518" />

        {/* Visual Thumbs */}
        <div className="absolute top-1/2 w-4 h-4 bg-white border-2 border-cyan-500 rounded-full shadow-lg pointer-events-none" style={{
        left: `${minPercent}%`,
        transform: 'translate(-50%, -50%)'
      }} data-id="element-519" />
        <div className="absolute top-1/2 w-4 h-4 bg-white border-2 border-cyan-500 rounded-full shadow-lg pointer-events-none" style={{
        left: `${maxPercent}%`,
        transform: 'translate(-50%, -50%)'
      }} data-id="element-520" />

        {/* Labels */}
        <div className="absolute -top-6 left-0 text-[10px] font-bold text-slate-400" data-id="element-521">
          {format(minVal)}
        </div>
        <div className="absolute -top-6 right-0 text-[10px] font-bold text-cyan-400" data-id="element-522">
          {format(maxVal)}
        </div>
      </div>
    </div>;
}